// Centralized alphabet management with multi-language support
import type { LanguageAlphabet } from "./types";
import type { LocaleCode } from "../api/client";

// Re-export types
export type { LanguageAlphabet, AlphabetSection, AlphabetLetter, AlphabetExample } from "./types";

// Import generated alphabet JSON files
import deEn from "./de-en.json";
import deEs from "./de-es.json";
import dePl from "./de-pl.json";
import deRu from "./de-ru.json";
import deUk from "./de-uk.json";
import esEn from "./es-en.json";
import esPl from "./es-pl.json";
import esRu from "./es-ru.json";
import esUk from "./es-uk.json";
import frEn from "./fr-en.json";
import frEs from "./fr-es.json";
import frPl from "./fr-pl.json";
import frRu from "./fr-ru.json";
import frUk from "./fr-uk.json";
import heEn from "./he-en.json";
import heEs from "./he-es.json";
import hePl from "./he-pl.json";
import heRu from "./he-ru.json";
import heUk from "./he-uk.json";
import itEn from "./it-en.json";
import itEs from "./it-es.json";
import itPl from "./it-pl.json";
import itRu from "./it-ru.json";
import itUk from "./it-uk.json";
import jaEn from "./ja-en.json";
import jaEs from "./ja-es.json";
import jaPl from "./ja-pl.json";
import jaRu from "./ja-ru.json";
import jaUk from "./ja-uk.json";
import koEn from "./ko-en.json";
import koEs from "./ko-es.json";
import koPl from "./ko-pl.json";
import koRu from "./ko-ru.json";
import koUk from "./ko-uk.json";
import plEn from "./pl-en.json";
import plEs from "./pl-es.json";
import plRu from "./pl-ru.json";
import plUk from "./pl-uk.json";
import ptEn from "./pt-en.json";
import ptEs from "./pt-es.json";
import ptPl from "./pt-pl.json";
import ptRu from "./pt-ru.json";
import ptUk from "./pt-uk.json";
import ruEn from "./ru-en.json";
import ruEs from "./ru-es.json";
import ruPl from "./ru-pl.json";
import ruUk from "./ru-uk.json";
import ukEn from "./uk-en.json";
import ukEs from "./uk-es.json";
import elDe from "./el-de.json";
import elEn from "./el-en.json";
import elEs from "./el-es.json";
import elPl from "./el-pl.json";
import elRu from "./el-ru.json";
import elUk from "./el-uk.json";
import zhEn from "./zh-en.json";
import zhEs from "./zh-es.json";
import zhPl from "./zh-pl.json";
import zhRu from "./zh-ru.json";
import zhUk from "./zh-uk.json";

// Import locale-specific alphabet JSON files
import enUSRu from "./en-US_ru.json";
import enGBRu from "./en-GB_ru.json";

// Locale-specific alphabet content (targetLocale_nativeLocale -> content)
// Use underscore to separate target and native locales since locales contain hyphens
const localeAlphabetContent: Record<string, LanguageAlphabet> = {
  // English (American) alphabet for Russian speakers
  "en-US_ru": enUSRu as LanguageAlphabet,
  // English (British) alphabet for Russian speakers
  "en-GB_ru": enGBRu as LanguageAlphabet,
};

// Alphabet content organized by target language -> native language (base codes)
const alphabetContent: Record<string, Record<string, LanguageAlphabet>> = {
  de: {
    en: deEn as LanguageAlphabet,
    es: deEs as LanguageAlphabet,
    pl: dePl as LanguageAlphabet,
    ru: deRu as LanguageAlphabet,
    uk: deUk as LanguageAlphabet,
  },
  el: {
    de: elDe as LanguageAlphabet,
    en: elEn as LanguageAlphabet,
    es: elEs as LanguageAlphabet,
    pl: elPl as LanguageAlphabet,
    ru: elRu as LanguageAlphabet,
    uk: elUk as LanguageAlphabet,
  },
  es: {
    en: esEn as LanguageAlphabet,
    pl: esPl as LanguageAlphabet,
    ru: esRu as LanguageAlphabet,
    uk: esUk as LanguageAlphabet,
  },
  fr: {
    en: frEn as LanguageAlphabet,
    es: frEs as LanguageAlphabet,
    pl: frPl as LanguageAlphabet,
    ru: frRu as LanguageAlphabet,
    uk: frUk as LanguageAlphabet,
  },
  he: {
    en: heEn as LanguageAlphabet,
    es: heEs as LanguageAlphabet,
    pl: hePl as LanguageAlphabet,
    ru: heRu as LanguageAlphabet,
    uk: heUk as LanguageAlphabet,
  },
  it: {
    en: itEn as LanguageAlphabet,
    es: itEs as LanguageAlphabet,
    pl: itPl as LanguageAlphabet,
    ru: itRu as LanguageAlphabet,
    uk: itUk as LanguageAlphabet,
  },
  ja: {
    en: jaEn as LanguageAlphabet,
    es: jaEs as LanguageAlphabet,
    pl: jaPl as LanguageAlphabet,
    ru: jaRu as LanguageAlphabet,
    uk: jaUk as LanguageAlphabet,
  },
  ko: {
    en: koEn as LanguageAlphabet,
    es: koEs as LanguageAlphabet,
    pl: koPl as LanguageAlphabet,
    ru: koRu as LanguageAlphabet,
    uk: koUk as LanguageAlphabet,
  },
  pl: {
    en: plEn as LanguageAlphabet,
    es: plEs as LanguageAlphabet,
    ru: plRu as LanguageAlphabet,
    uk: plUk as LanguageAlphabet,
  },
  pt: {
    en: ptEn as LanguageAlphabet,
    es: ptEs as LanguageAlphabet,
    pl: ptPl as LanguageAlphabet,
    ru: ptRu as LanguageAlphabet,
    uk: ptUk as LanguageAlphabet,
  },
  ru: {
    en: ruEn as LanguageAlphabet,
    es: ruEs as LanguageAlphabet,
    pl: ruPl as LanguageAlphabet,
    uk: ruUk as LanguageAlphabet,
  },
  uk: {
    en: ukEn as LanguageAlphabet,
    es: ukEs as LanguageAlphabet,
  },
  zh: {
    en: zhEn as LanguageAlphabet,
    es: zhEs as LanguageAlphabet,
    pl: zhPl as LanguageAlphabet,
    ru: zhRu as LanguageAlphabet,
    uk: zhUk as LanguageAlphabet,
  },
};

// Get base language code
function getBaseCode(code: string): string {
  return code.split("-")[0]!;
}

// Get the appropriate alphabet based on target language and native language
// Supports full locale codes (e.g., "en-US", "ru-RU") with fallback chain:
// 1. Exact locale pair (en-US_ru-RU)
// 2. Target locale with base native (en-US_ru)
// 3. Base codes only (en_ru) - backwards compatible
// 4. Target with English fallback (en_en)
export function getAlphabet(targetLangCode: LocaleCode, nativeLangCode: LocaleCode): LanguageAlphabet | null {
  const targetBase = getBaseCode(targetLangCode);
  const nativeBase = getBaseCode(nativeLangCode);

  // 1. Try exact locale pair
  const exactKey = `${targetLangCode}_${nativeLangCode}`;
  if (localeAlphabetContent[exactKey]) {
    return localeAlphabetContent[exactKey];
  }

  // 2. Try target locale with base native
  const targetLocaleBaseNativeKey = `${targetLangCode}_${nativeBase}`;
  if (localeAlphabetContent[targetLocaleBaseNativeKey]) {
    return localeAlphabetContent[targetLocaleBaseNativeKey];
  }

  // 3. Try base codes (backwards compatible with existing content)
  const targetAlphabet = alphabetContent[targetBase];
  if (targetAlphabet) {
    // Try exact native base match first, then fallback to English
    if (targetAlphabet[nativeBase]) {
      return targetAlphabet[nativeBase];
    }
    if (targetAlphabet["en"]) {
      return targetAlphabet["en"];
    }
  }

  return null;
}

